import { MapPin, Phone, Mail, Clock, ExternalLink } from 'lucide-react';
import { LocalizedLink } from '@/components/LocalizedLink';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { useT } from '@/lib/i18n';

export function Footer() {
  const t = useT();

  const footerLinks = {
    unternehmen: [
      { label: t('Über uns', 'About'), href: '/ueber-uns' },
      { label: t('Consulting', 'Consulting'), href: '/consulting' },
      { label: t('Maschinenpark', 'Machine Park'), href: '/maschinenpark' },
      { label: t('Nachhaltigkeit', 'Sustainability'), href: '/nachhaltigkeit' },
      { label: 'Blog', href: '/blog' },
      { label: t('Karriere', 'Careers'), href: '/karriere' },
    ],
    leistungen: [
      { label: t('CNC-Drehen', 'CNC Turning'), href: '/leistungen#drehen' },
      { label: t('CNC-Fräsen', 'CNC Milling'), href: '/leistungen#fraesen' },
      { label: t('Baugruppen', 'Assemblies'), href: '/leistungen#baugruppen' },
      { label: 'CAD/CAM', href: '/leistungen#cad' },
    ],
    rechtliches: [
      { label: t('Impressum', 'Legal Notice'), href: '/impressum' },
      { label: t('Datenschutz', 'Privacy Policy'), href: '/datenschutz' },
      { label: t('AGB', 'Terms'), href: '/agb' },
    ],
  };

  return (
    <footer className="bg-card border-t border-border/30">
      <div className="container mx-auto px-4 sm:px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary rounded flex items-center justify-center">
                <span className="text-primary-foreground font-bold text-xl">F</span>
              </div>
              <div className="flex flex-col">
                <span className="text-lg font-bold tracking-tight">FERNAU</span>
                <span className="text-xs text-muted-foreground font-mono tracking-wider">
                  {t('PRÄZISIONSTECHNIK', 'PRECISION ENGINEERING')}
                </span>
              </div>
            </div>
            <p className="text-muted-foreground text-sm leading-relaxed">
              {t(
                'Ihr kompetenter Partner für CNC-Zerspanung seit 1995. Präzision, Qualität und Termintreue. Made in Darmstadt.',
                'Your trusted partner for CNC machining since 1995. Precision, quality and on-time delivery. Made in Darmstadt, Germany.',
              )}
            </p>
            <div className="flex items-center gap-2 text-xs font-mono text-primary">
              <span className="w-2 h-2 bg-primary rounded-full animate-pulse" />
              {t('ISO 9001:2015 ZERTIFIZIERT', 'ISO 9001:2015 CERTIFIED')}
            </div>
            <LanguageSwitcher variant="footer" />
          </div>

          <div>
            <h4 className="font-semibold mb-6 text-sm uppercase tracking-wider">
              {t('Unternehmen', 'Company')}
            </h4>
            <ul className="space-y-3">
              {footerLinks.unternehmen.map((link) => (
                <li key={link.href}>
                  <LocalizedLink
                    to={link.href}
                    className="text-muted-foreground hover:text-foreground transition-colors text-sm"
                  >
                    {link.label}
                  </LocalizedLink>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-6 text-sm uppercase tracking-wider">
              {t('Leistungen', 'Services')}
            </h4>
            <ul className="space-y-3">
              {footerLinks.leistungen.map((link) => (
                <li key={link.href}>
                  <LocalizedLink
                    to={link.href}
                    className="text-muted-foreground hover:text-foreground transition-colors text-sm"
                  >
                    {link.label}
                  </LocalizedLink>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-6 text-sm uppercase tracking-wider">
              {t('Kontakt', 'Contact')}
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-0.5 text-primary shrink-0" />
                <address className="not-italic text-sm text-muted-foreground">
                  Merianstraße 5a<br />
                  64291 Darmstadt{t('', ', Germany')}
                </address>
              </li>
              <li>
                <a
                  href="tel:+4961501849730"
                  className="flex items-center gap-3 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Phone className="w-4 h-4 text-primary shrink-0" />
                  <span className="font-mono">{t('06150 184973-0', '+49 6150 184973-0')}</span>
                </a>
              </li>
              <li>
                <a
                  href="mailto:info@fernau-gmbh.de"
                  className="flex items-center gap-3 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Mail className="w-4 h-4 text-primary shrink-0" />
                  info@fernau-gmbh.de
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="w-4 h-4 mt-0.5 text-primary shrink-0" />
                <div className="text-sm text-muted-foreground">
                  <p>{t('Mo-Do: 7:30 - 16:00 Uhr', 'Mon-Thu: 7:30 - 16:00')}</p>
                  <p>{t('Fr: 7:30 - 12:30 Uhr', 'Fri: 7:30 - 12:30')}</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-t border-border/30">
        <div className="container mx-auto px-4 sm:px-6 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} FERNAU Präzisionstechnik GmbH.{' '}
              {t('Alle Rechte vorbehalten.', 'All rights reserved.')}
            </p>
            <div className="flex items-center gap-6">
              {footerLinks.rechtliches.map((link) => (
                <LocalizedLink
                  key={link.href}
                  to={link.href}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  {link.label}
                </LocalizedLink>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-secondary/30 border-t border-border/20">
        <div className="container mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-center gap-3 text-xs text-muted-foreground">
            <span>{t('Mitglied im', 'Member of')}</span>
            <a
              href="https://www.bnw-bundesverband.de/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-primary hover:underline"
            >
              {t(
                'Bundesverband Nachhaltige Wirtschaft',
                'German Federal Association for Sustainable Economy',
              )}
              <ExternalLink className="w-3 h-3" aria-hidden="true" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}